import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, OverlayContainer } from '@angular/cdk/overlay';

import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, Component, DOCUMENT, HostListener, Inject, Input, OnDestroy, OnInit, Pipe, PipeTransform, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { ServiceItem } from '@shared/serviceitem';
import { IAccount, IOrganization, UserService } from '@shared/services/user.service';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { SidenavService } from '@shared/services/sidenav.service';
import { LocalStorageService, SessionStorageService } from '@shared/services/storage.service';
import { SyncMonitoringService } from '../admin/sync-monitoring/sync-monitoring.service';
import { IEnvironmentSummary } from '../admin/sync-monitoring/sync-monitoring.models';
import { asapScheduler, catchError, filter, map, merge, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { AccountListItemComponent } from './account-list-item/account-list-item.component';
import { serviceItems } from './servicemenu';

const MANUAL_URL = 'https://aciesdocs.gitbook.io/aciesone-manual';

export interface IData {
	organization: IOrganization;
	accounts: IAccount[];
}

export interface IEnvironmentSelectorItem {
	environmentId: string;
	engineId: string;
	name: string;
}

@Component({
	selector: 'app-topbar',
	templateUrl: './topbar.component.html',
	styleUrls: ['./topbar.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TopbarComponent implements OnInit, OnDestroy, AfterViewInit {
	public theme: string = 'my-light-theme';
	public color: string = 'accent';
	public userEmail: string = '';
	@Input() userAccount: string = '-';
	public isOpen: boolean = false;
	public isOpenServices: boolean = false;
	public selected: string = '';
	public filter: UntypedFormControl = new UntypedFormControl('');
	public scrWidth: number;
	public scrHeight: number;
	public permissions: string[] = [];
	public isAccountAdmin: boolean = false;

	@Input() isLoggedIn;

	public data$: Observable<IData[]> = of([]);
	public dataFiltered$: Observable<IData[]>;
	@Input() accounts: IAccount[] = [];
	public services: ServiceItem[] = serviceItems;
	public selectedServiceName = '';
	public filtercond: object = { enabled: true };
	public defaultAccount: IAccount;
	private destroyNotifier$ = new Subject<void>();
	public unreadCount: number = 0;
	public unreadError: boolean = false;
	public accountsLoading = false;
	public isExpanded: boolean = true;
	public hasSuperadminPermission = false;
	public engineEnvironments: IEnvironmentSelectorItem[] = [];
	public selectedEnvironment: IEnvironmentSelectorItem | null = null;
	private engineEnvironmentsAccountId: string | null = null;

	@ViewChildren(AccountListItemComponent)
	items: QueryList<AccountListItemComponent>;

	private keyManager: ActiveDescendantKeyManager<AccountListItemComponent>;

	public readonly overlayOptions: Partial<CdkConnectedOverlay> = {
		hasBackdrop: true,
		backdropClass: 'cdk-overlay-transparent-backdrop',
		positions: [
			{
				originX: 'end',
				originY: 'bottom',
				overlayX: 'end',
				overlayY: 'top',
			},
		],
	};

	public theTerminalClass = 'my-light-theme';

	constructor(
		private sessionStorageService: SessionStorageService,
		private router: Router,
		@Inject(DOCUMENT) private document: Document,
		public http: HttpClient,
		private overlayContainer: OverlayContainer,
		private userService: UserService,
		public dialog: MatDialog,
		public sidenavService: SidenavService,
		private translate: TranslateService,
		private authService: AuthService,
		private localStorageService: LocalStorageService,
		public notificationService: NotificationService,
		private translateService: TranslateService,
		private userPermissionsService: UserPermissionsService,
		private syncMonitoringService: SyncMonitoringService,
	) {
		this.sessionStorageService
			.watch('Selected.ServiceName')
			.pipe(takeUntil(this.destroyNotifier$))
			.subscribe((serviceName) => {
				this.selectedServiceName = serviceName;
			});

		router.events.subscribe(() => {
			if (location.href.endsWith('/terminal') || location.href.includes('/sales/configurator') || location.href.endsWith('/sales/configuration') || (location.href.endsWith('/core') && this.localStorageService.get('User.DeepLink')?.includes('sales/configurator'))) {
				this.theTerminalClass = 'my-light-theme terminal';
			} else {
				this.theTerminalClass = 'my-light-theme';
			}
		});
		// we need to wait for the user permissions to be loaded, so subscribe
		// here and set the permission flag when the permissions data is available
		userPermissionsService.userPermissions$.pipe(takeUntil(this.destroyNotifier$)).subscribe(() => {
			this.hasSuperadminPermission = this.userPermissionsService.hasPermissions(['iam:OrganizationsPut']);
		});
	}

	ngAfterViewInit(): void {
		this.keyManager = new ActiveDescendantKeyManager(this.items).withWrap().withTypeAhead();
	}

	ngOnDestroy(): void {
		this.destroyNotifier$.next();
		this.destroyNotifier$.complete();
	}

	@HostListener('window:resize', ['$event'])
	getScreenSize() {
		this.scrHeight = window.innerHeight;
		this.scrWidth = window.innerWidth;
	}

	// For iphone
	@HostListener('window:orientationchange', ['$event.target.orientation'])
	getScreenSizePhone(orientation) {
		setTimeout(() => {
			if (orientation === 0) {
				// portrait: orientation = 0
				this.scrHeight = screen.height;
				this.scrWidth = screen.width;
			} else {
				// landscape: orientation = 90 eller orientation = -90
				// Hvis vi er i landscape, skal højde og bredde lige byttes rundt...
				this.scrHeight = screen.width;
				this.scrWidth = screen.height;
			}
		}, 0);
	}

	ngOnInit(): void {
		this.scrHeight = window.innerHeight;
		this.scrWidth = window.innerWidth;

		this.notificationService.unreadCount$.pipe(takeUntil(this.destroyNotifier$)).subscribe((count) => {
			this.unreadCount = count;
		});

		this.notificationService.errorNotifications$.pipe(takeUntil(this.destroyNotifier$)).subscribe((val) => {
			this.unreadError = val;
		});

		this.userService.account$.pipe(takeUntil(this.destroyNotifier$)).subscribe((v) => {
			this.selected = v?.id;
			this.filter.setValue('');
			this.loadEngineEnvironments(v);
		});

		this.authService.isLoggedIn$.pipe(takeUntil(this.destroyNotifier$)).subscribe((loggedIn) => {
			if (!loggedIn) {
				this.hasSuperadminPermission = false;
				return;
			}

			this.isLoggedIn = loggedIn;
			this.userEmail = this.sessionStorageService.get('User.Email');
			this.accountsLoading = true;

			this.userService.fetchAccountsObs().subscribe({
				next: (ua) => {
					if (!this.isLoggedIn) {
						return;
					}
					this.accountsLoading = false;

					// Set isAccountAdmin if user has any permissions or is AccountManager in any org or nested account
					const hasAccountManager = ua.result.organizations?.some((org) => org.permissions?.includes('iam:AccountManager') || org.accounts?.some((acc) => acc.permissions?.includes('iam:AccountManager')));
					// Permissions are usually account-scoped (result.permissions is often empty), so also
					// treat "holds any Administration-gating permission on an org/account" as account-admin —
					// otherwise the Administration menu stays hidden for users who CAN open a page under it
					// (e.g. a sync-monitoring-only user with just one:EnvironmentGet).
					const adminPermissions = (serviceItems.find((s) => s.label === 'Administration')?.permission ?? '').split('|');
					const hasAdminPermission = ua.result.organizations?.some((org) => org.permissions?.some((p) => adminPermissions.includes(p)) || org.accounts?.some((acc) => acc.permissions?.some((p) => adminPermissions.includes(p))));
					if (ua.result.permissions.length > 0 || hasAccountManager || hasAdminPermission) {
						this.isAccountAdmin = true;
					}

					this.data$ = this.userService.accounts$.pipe(
						filter((v) => v.length > 0),
						map((r) =>
							r.reduce((a, v) => {
								if (!a.find((o) => o.organization.id === v.organization.id)) {
									a.push({
										organization: v.organization,
										accounts: [],
									});
								}
								a.find((o) => o.organization.id === v.organization.id)?.accounts.push(v);

								return a;
							}, [] as IData[]),
						),
					);

					this.dataFiltered$ = merge(this.data$, this.filter.valueChanges).pipe(
						takeUntil(this.destroyNotifier$),
						switchMap(() => this.data$),
						map((v) => {
							const searchTerm = this.filter.value?.toLowerCase() ?? '';
							if (!searchTerm) {
								return v;
							}

							return v
								.map((o) => {
									if (o.organization.name.toLowerCase().includes(searchTerm)) {
										return o;
									}

									return {
										...o,
										accounts: o.accounts.filter((account) => account.name.toLowerCase().includes(searchTerm)),
									};
								})
								.filter((o) => o.accounts.length > 0);
						}),
					);
				},
				error: (error) => {
					this.data$ = of([]);
					console.log(JSON.stringify(error));
				},
			});

			if (!this.sessionStorageService.get('User.Account')) {
				this.translate.get('topbar.account.unknown').subscribe((label: string) => {
					this.userAccount = label;
				});
			}

			this.sessionStorageService.watch('User.Account').subscribe((account) => {
				const acc: IAccount = JSON.parse(account);
				if (acc != null) {
					this.userAccount = `${acc.organization.name} > ${acc.name}`;
				}
				this.userService.account = acc;
			});
		});
	}

	onKeydown(event: any) {
		if (event.keyCode === 13) {
			this.userService.account = this.keyManager.activeItem.item;
			this.selectAccount(this.keyManager.activeItem.item);
		} else {
			this.keyManager.onKeydown(event);
		}
	}

	selectAciesCoreServiceByName(serviceItem: string): void {
		const selectedService = this.services.find((s) => s.label === serviceItem);
		if (selectedService) {
			this.selectAciesCoreService([selectedService]);
		}
	}

	serviceListSelectionChanged(event: MatSelectionListChange): void {
		if (event.options.at(0)) {
			const selected = event.options.at(0);
			const value = selected.value as ServiceItem;
			this.selectAciesCoreService([value]);
		}
	}

	selectAciesCoreService([serviceItem]: [ServiceItem]): void {
		this.sessionStorageService.remove('Navigation.DeepLink');
		// this is a special override for SUPER_ADMINS that checks if the
		// route is "setup" and if the user has SUPER_ADMIN permissions
		if (serviceItem.link.startsWith('setup') && this.hasSuperadminPermission) {
			this.navigateToServiceItemLink(serviceItem);
			return;
		}
		if (this.userService.account == null && !this.defaultAccount) {
			// No account selected - do not start service
			this.sessionStorageService.set('RedirectToService', JSON.stringify(serviceItem));
			this.openSelectAccountDialog(serviceItem);
		} else {
			this.navigateToServiceItemLink(serviceItem);
		}
	}

	openSelectAccountDialog(serviceItem: ServiceItem): void {
		const dialogRef = this.dialog.open(DialogNoSelectServiceDialogComponent, {
			width: '400px',
			data: serviceItem.label,
		});
		dialogRef.afterClosed().subscribe(() => {
			this.isOpen = true; // this.isOpen refers to the "open" status of the account selection overlay
		});
	}

	navigateToServiceItemLink(serviceItem: ServiceItem): void {
		if (serviceItem.label) {
			this.sessionStorageService.set('Selected.ServiceName', serviceItem.forceTranslateKey);
			this.localStorageService.set('User.Selected.ServiceName', serviceItem.label);
		}
		void this.router.navigate([serviceItem.link]);
		this.isOpenServices = false;
	}

	onLogout(event?): void {
		event.stopPropagation();

		const dialogRef = this.dialog.open(DialogLogoutConfirm, {
			width: '400px',
			panelClass: 'global-dialog-class',
		});

		dialogRef
			.afterClosed()
			.pipe(filter((s) => s === true))
			.subscribe(() => {
				this.isLoggedIn = false;
				this.clearEngineEnvironments();
				const adlogin = this.sessionStorageService.get('AdLogin');
				this.sessionStorageService.remove('AdLogin');
				this.isOpen = false;
				this.authService.loggedInStatus.next(false);
				if (adlogin === 'true' && environment.production === true) {
					this.sessionStorageService.remove('User.AzureClientId');
					this.handleLogoutAndRememberMe();
					void this.router.navigate(['core', 1]);
				} else {
					this.handleLogoutAndRememberMe();
					asapScheduler.schedule(() => {
						void this.router.navigate(['signin']);
					}, 0);
				}
			});
	}

	handleLogoutAndRememberMe() {
		const tempUser = this.localStorageService.get('User.Email');
		const tempRemember = this.localStorageService.get('User.RememberMe');
		const tempAccount = this.localStorageService.get('User.Selected.Account');
		const tempIsExpanded = this.localStorageService.get('User.Selected.SideNav.IsExpanded');
		const tempRightIsExpanded = this.localStorageService.get('User.Selected.SideNav.RightIsExpanded');
		const tempSelectedServiceName = this.localStorageService.get('User.Selected.ServiceName');
		this.localStorageService.clear();
		this.localStorageService.set('User.Email', tempUser);
		this.localStorageService.set('User.RememberMe', tempRemember);
		if (tempRemember === 'true') {
			this.localStorageService.set('User.Selected.ServiceName', tempSelectedServiceName);
			this.localStorageService.set('User.Selected.SideNav.IsExpanded', tempIsExpanded);
			this.localStorageService.set('User.Selected.SideNav.RightIsExpanded', tempRightIsExpanded);
			this.localStorageService.set('User.Selected.Account', tempAccount);
		}
	}

	selectAccount(account: IAccount | null) {
		this.userAccount = `${account.organization.name} > ${account.name}`;
		this.keyManager.setActiveItem(this.items.find((x) => x.getLabel() === account.name));
		this.isOpen = false;
		this.authService.selectAccount(account, this.services);
	}

	@HostListener('window:keydown.control.l', ['$event'])
	doLogout(event: KeyboardEvent) {
		event.preventDefault();
		this.onLogout();
	}

	@HostListener('window:keydown.control.alt.a', ['$event'])
	doActivateAccountOverlay(event: KeyboardEvent) {
		event.preventDefault();
		const element = document.getElementById('accountList');
		element.click();
	}

	@HostListener('window:keydown.control.s', ['$event'])
	doActivateServiceOverlay(event: KeyboardEvent) {
		event.preventDefault();
		const element = document.getElementById('servicesList');
		element.click();
	}

	hideOverlay(_: Event) {
		this.isOpen = false;
		this.filter.setValue('');
	}

	hideServiceOverlay(_: Event) {
		this.isOpenServices = false;
	}

	helpClicked(): void {
		const language = this.translateService.currentLang === 'en' ? '/eng' : '';
		window.open(`${MANUAL_URL}${language}/core/core`, '_blank', 'noopener');
	}

	setSearchFocus() {
		setTimeout(() => {
			if (this.isOpen) {
				document.getElementById('accountSearch').focus();
			}
		}, 200);
	}

	getChecketLanguageIcon(language: string): string {
		return this.sessionStorageService.get('User.PreferredLanguage') === language ? 'done' : '';
	}

	setLang(lang: string) {
		if (lang === 'reset') {
			lang = this.translateService.getBrowserLang();
		}
		this.sessionStorageService.set('User.PreferredLanguage', lang);
		this.translateService.use(lang);
	}

	private loadEngineEnvironments(account: IAccount | null): void {
		this.clearEngineEnvironments();

		if (!account) {
			return;
		}

		this.engineEnvironmentsAccountId = account.id;
		this.syncMonitoringService
			.getEnvironments(account)
			.pipe(
				takeUntil(this.destroyNotifier$),
				map((response) => this.normalizeEnvironmentResponse(response)),
				catchError((error: unknown) => {
					console.log('Failed to load engine environments', error);
					return of([] as IEnvironmentSelectorItem[]);
				}),
			)
			.subscribe((environments) => {
				if (this.engineEnvironmentsAccountId !== account.id) {
					return;
				}

				this.engineEnvironments = environments;
				this.sessionStorageService.set('User.Environments', JSON.stringify(environments));
				this.selectDefaultEngineEnvironment(account, environments);
			});
	}

	private clearEngineEnvironments(): void {
		this.engineEnvironmentsAccountId = null;
		this.engineEnvironments = [];
		this.selectedEnvironment = null;
		this.sessionStorageService.set('User.Environments', JSON.stringify([]));
		this.sessionStorageService.remove('User.SelectedEnvironment');
	}

	private normalizeEnvironmentResponse(environments: IEnvironmentSummary[]): IEnvironmentSelectorItem[] {
		const selectorItems = environments.map((item) => this.normalizeEnvironmentItem(item)).filter((item): item is IEnvironmentSelectorItem => item !== null);
		const seen = new Set<string>();

		return selectorItems
			.filter((environment) => {
				const key = `${environment.environmentId}:${environment.engineId}`;
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			})
			.sort((left, right) =>
				left.name.localeCompare(right.name, undefined, {
					numeric: true,
					sensitivity: 'base',
				}),
			);
	}

	private normalizeEnvironmentItem(environment: IEnvironmentSummary): IEnvironmentSelectorItem | null {
		const environmentId = this.extractId(environment.self);
		const engineId = this.environmentEngineId(environment);

		if (!environmentId || !engineId) {
			return null;
		}

		return { environmentId, engineId, name: environment.name };
	}

	private environmentEngineId(environment: IEnvironmentSummary): string | null {
		const value = environment.engineId ?? environment.EngineId ?? environment.engine ?? environment.Engine;
		return typeof value === 'string' && value.length > 0 ? value : typeof value === 'number' ? String(value) : null;
	}

	private extractId(self: string | null): string | null {
		return self?.split('/').filter(Boolean).pop() ?? null;
	}

	private selectDefaultEngineEnvironment(account: IAccount, environments: IEnvironmentSelectorItem[]): void {
		if (environments.length === 0) {
			this.sessionStorageService.remove('User.SelectedEnvironment');
			return;
		}

		const defaultEngine = account.defaultEngine ? String(account.defaultEngine) : null;
		const selected = (defaultEngine ? environments.find((environment) => environment.engineId === defaultEngine) : null) ?? environments[0];

		this.selectEngineEnvironment(selected);
	}

	selectEngineEnvironment(environment: IEnvironmentSelectorItem): void {
		this.selectedEnvironment = environment;
		this.sessionStorageService.set('User.SelectedEnvironment', JSON.stringify(environment));
	}

	getCheckedEnvironmentIcon(environment: IEnvironmentSelectorItem): string {
		return this.selectedEnvironment?.environmentId === environment.environmentId && this.selectedEnvironment?.engineId === environment.engineId ? 'done' : '';
	}
}

@Component({
	selector: 'app-dialog-no-start-service',
	templateUrl: 'dialog-no-start-service.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DialogNoSelectServiceDialogComponent {
	constructor(
		public dialogRef: MatDialogRef<DialogNoSelectServiceDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: string,
	) {}

	onNoClick(): void {
		this.dialogRef.close('closed');
	}
}

@Component({
	selector: 'app-dialog-logout-confirm',
	templateUrl: 'dialog-logout-confirm.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DialogLogoutConfirm {
	constructor(
		public dialogRef: MatDialogRef<DialogLogoutConfirm>,
		@Inject(MAT_DIALOG_DATA) public data: string,
	) {}

	onNoClick(): void {
		this.dialogRef.close(false);
	}

	onYesClick(): void {
		this.dialogRef.close(true);
	}
}

@Pipe({
	name: 'customFilter',
	pure: false,
	standalone: false,
})
export class CustomFilterPipe implements PipeTransform {
	permissions: string[];
	constructor(
		private router: Router,
		private sessionStorageService: SessionStorageService,
	) {
		const storagePermissions = this.sessionStorageService.get('User.Permissions');
		this.permissions = storagePermissions !== null ? JSON.parse(storagePermissions) : [];
	}

	transform(items: any[], filter: object): any {
		if (!items || !filter) {
			return items;
		}
		if (this.router.url.substring(1) === 'invite') {
			return [];
		}
		// filter items array, items which match and return true will be
		// kept, false will be filtered out
		return items.filter((item) => {
			if (item !== null && item.label.toLowerCase() === 'sales') {
				return this.sessionStorageService.get('User.Email')?.toLowerCase().includes('@acies.dk') || this.sessionStorageService.get('User.Email')?.toLowerCase().includes('@elitfonster.se');
			}
			const permissions = item.permission.split('|');
			return (
				item.enabled === true &&
				item.showInServiceMenu &&
				(item.permission === '' ||
					permissions.some((permission) => {
						return this.permissions.includes(permission);
					}))
			);
		});
	}
}
